// @flow
import get from 'lodash/get';
import groupBy from 'lodash/groupBy';
import isEmpty from 'lodash/isEmpty';
import mapValues from 'lodash/mapValues';
import omit from 'lodash/omit';
import sortBy from 'lodash/sortBy';
import toPairs from 'lodash/toPairs';
import uniq from 'lodash/uniq';
import uniqBy from 'lodash/uniqBy';

import { createAction } from 'redux-act';

import {
  getCart,
  updateProducts,
  removeCartProducts,
  selectCartReselleeID,
  selectCartProductOwner,
  selectDomainProductsToPatchWithOwner,
  selectRawProductsArray,
} from '@gandi/cart';

import {
  getBaseContactFields,
  getBaseContactFieldsField,
  validateContactValues,
  type Process,
  type Tld,
  type FormValues,
  type Role,
  // $FlowFixMe[missing-export]
  type OrganizationType,
} from '@gandi/domain-configuration';
import {
  fetchOneOrganization,
  selectOneOrganization,
} from '@gandi/redux-organization';
import {
  contactbook,
  type FavoriteContact,
  type ExtraParams,
} from '@gandi/redux-resource-contactbook';
import type { Organization } from '@gandi/redux-sharing';
import {
  createResource,
  type FetchResponse,
  type Resource,
} from '@gandi/resource/redux';
import type { TranslatorProps } from '@gandi/translate';

import { logging } from 'gandi.redux-logger';

import type {
  Dispatch,
  GetState,
  ResourceThunkAction,
  ThunkAction,
} from 'src/types';

import {
  patchDomainContactsStart,
  patchDomainContactsEnd,
  type PatchDomainContactsEndAction,
} from '../../../../modules/app';

import { fetchCorporateTrusteePrices } from '../../../../modules/app/actions';
import { selectShopCurrent } from '../../../../modules/organization/selectors';

import {
  selectOneCustomer,
  fetchOneCustomer,
} from '../../../Customer/modules/customers';

import {
  selectCurrentContactByRoles,
  selectSetupOwner,
  selectShouldFetchFavoriteContacts,
  selectShouldFetchOneContactExtraParams,
  selectShouldFetchOwnerExtraParams,
} from '../../../Setup/modules/selectors';
import { selectDomainOwnerId } from '../../../modules/selectors';
import type {
  Fqdn,
  Step,
  ProductPayload,
  InnerStep,
  InnerStepFlag,
  ProductDomainInfo,
  PurchaseGroupId,
} from '../types';

import {
  selectFormattedPayload,
  selectCurrentStep,
  selectCurrentStepInitialValues,
  selectProductsToPatchOrRemove,
  selectWizardHasTrusteeTlds,
  selectTrusteePrices,
  selectTrusteeRoleFlag,
  selectProductsWithDomainContactError,
} from './selectors';

const loggerNamespace = 'Configure/DomainContacts/modules';

/**
 * Actions
 */

export const resetState: {
  (...mixed): { meta?: mixed, payload: mixed, type: string, ... },
  assignTo: mixed => void,
  assigned: () => boolean,
  bindTo: mixed => mixed,
  bound: () => boolean,
  dispatched: () => boolean,
  getType: () => string,
  raw: () => mixed,
  toString: () => string,
  ...
} = createAction('[DomainContacts] Reset the wizard state.');

export const prepareState: {
  (...Array<{ products: Array<ProductDomainInfo> }>): {
    meta?: mixed,
    payload: mixed,
    type: string,
    ...
  },
  assignTo: mixed => void,
  assigned: () => boolean,
  bindTo: mixed => mixed,
  bound: () => boolean,
  dispatched: () => boolean,
  getType: () => string,
  raw: () => mixed,
  toString: () => string,
  ...
} = createAction('Prepare the state from the list of domains');

export const saveStepValues: any = createAction(
  'Save form values for a step',
  (step: Step, values: FormValues) => ({ step, values }),
);

/* BULK ACTIONS */
export const removeDomainsStart: {
  (...Array<any>): { meta?: mixed, payload: mixed, type: string, ... },
  assignTo: mixed => void,
  assigned: () => boolean,
  bindTo: mixed => mixed,
  bound: () => boolean,
  dispatched: () => boolean,
  getType: () => string,
  raw: () => mixed,
  toString: () => string,
  ...
} = createAction('[DomainContacts] Start remove a domain from the wizard.');

export const removeDomainsFromWizard: {
  (...Array<any> | Array<Process | Tld | PurchaseGroupId>): {
    meta?: mixed,
    payload: { process: Process, purchaseGroupId: PurchaseGroupId, tld: Tld },
    type: string,
    ...
  },
  assignTo: mixed => void,
  assigned: () => boolean,
  bindTo: mixed => mixed,
  bound: () => boolean,
  dispatched: () => boolean,
  getType: () => string,
  raw: () => mixed,
  toString: () => string,
  ...
} = createAction(
  '[DomainContacts] Remove a domain from the wizard.',
  (purchaseGroupId: PurchaseGroupId, tld: Tld, process: Process) => ({
    purchaseGroupId,
    tld,
    process,
  }),
);

export const removeDomainsSuccess: {
  (...Array<any>): { meta?: mixed, payload: mixed, type: string, ... },
  assignTo: mixed => void,
  assigned: () => boolean,
  bindTo: mixed => mixed,
  bound: () => boolean,
  dispatched: () => boolean,
  getType: () => string,
  raw: () => mixed,
  toString: () => string,
  ...
} = createAction('[DomainContacts] Remove a domain from the wizard - Success');

export const removeDomainsFail: {
  (...Array<any>): { meta?: mixed, payload: mixed, type: string, ... },
  assignTo: mixed => void,
  assigned: () => boolean,
  bindTo: mixed => mixed,
  bound: () => boolean,
  dispatched: () => boolean,
  getType: () => string,
  raw: () => mixed,
  toString: () => string,
  ...
} = createAction('[DomainContacts] Remove a domain from the wizard - Failure');

export const validateContactsStart: {
  (...Array<any>): { meta?: mixed, payload: mixed, type: string, ... },
  assignTo: mixed => void,
  assigned: () => boolean,
  bindTo: mixed => mixed,
  bound: () => boolean,
  dispatched: () => boolean,
  getType: () => string,
  raw: () => mixed,
  toString: () => string,
  ...
} = createAction('[DomainContacts] Start validation process');

export const validateContactsEnd: {
  (...Array<any>): { meta?: mixed, payload: mixed, type: string, ... },
  assignTo: mixed => void,
  assigned: () => boolean,
  bindTo: mixed => mixed,
  bound: () => boolean,
  dispatched: () => boolean,
  getType: () => string,
  raw: () => mixed,
  toString: () => string,
  ...
} = createAction('[DomainContacts] End validation process');

export const incrementCurrentStepIndex: {
  (...Array<any>): { meta?: mixed, payload: mixed, type: string, ... },
  assignTo: mixed => void,
  assigned: () => boolean,
  bindTo: mixed => mixed,
  bound: () => boolean,
  dispatched: () => boolean,
  getType: () => string,
  raw: () => mixed,
  toString: () => string,
  ...
} = createAction('[DomainContacts] Increment current step index');

export const setPatchContactsFlag: {
  (...mixed): { meta?: mixed, payload: mixed, type: string, ... },
  assignTo: mixed => void,
  assigned: () => boolean,
  bindTo: mixed => mixed,
  bound: () => boolean,
  dispatched: () => boolean,
  getType: () => string,
  raw: () => mixed,
  toString: () => string,
  ...
} = createAction(
  '[DomainContacts] Set flag to force patching the contacts on cart overview',
);

export const setInnerStepFlag: any = createAction(
  '[DomainContacts] Toggle value of ShowInnerStep for a tld and a process',
  (innerStep: InnerStep, flag: InnerStepFlag, roleHasTrustee: boolean) => ({
    innerStep,
    flag,
    roleHasTrustee,
  }),
);

export const addTrusteeToRole: {
  (...Array<void | FormValues | Role | Fqdn | Step>): {
    meta?: mixed,
    payload: { fqdn: Fqdn, role: Role, step: Step, values: FormValues },
    type: string,
    ...
  },
  assignTo: mixed => void,
  assigned: () => boolean,
  bindTo: mixed => mixed,
  bound: () => boolean,
  dispatched: () => boolean,
  getType: () => string,
  raw: () => mixed,
  toString: () => string,
  ...
} = createAction(
  '[DomainContacts] Add trustee option to role',
  (fqdn: Fqdn, role: Role, values: FormValues, step: Step) => ({
    fqdn,
    role,
    values,
    step,
  }),
);

export const setTrusteeRoleFlag: any = createAction(
  '[DomainContacts] Set flag to add trustee to role',
  (role: Role) => role,
);

export const resetTrusteeRoleFlag: {
  (...Array<any>): { meta?: mixed, payload: mixed, type: string, ... },
  assignTo: mixed => void,
  assigned: () => boolean,
  bindTo: mixed => mixed,
  bound: () => boolean,
  dispatched: () => boolean,
  getType: () => string,
  raw: () => mixed,
  toString: () => string,
  ...
} = createAction('[DomainContacts] reset the trustee role flag');

// Map properties used by organization API to the ones expected by contact validation.
const fieldsMapping = {
  given: 'firstname',
  family: 'lastname',
};

export const globalCheckResource: Resource<> = createResource(
  'globalCheckResource',
);

// XXX: not completely sure of this type (based on usage).
type BaseContact = {
  [string]: void | boolean | Organization,
  extra_parameters: { vat_number: any },
};

/**
 * Try and pre-fill base contact object
 * using organization data and assigning default values.
 *
 * By now, the values object should look like this in best-case scenario:
 * {
 *   // Organization data
 *   type: 0,
 *   given: 'John',
 *   family: 'Doe',
 *   country: 'FR',
 *   state: 'FR-J',
 *   city: 'Paris',
 *   zip: 75013,
 *   streetaddr: '63-65 boulevard Massena',
 *   phone: '+33677889955',
 *   email: 'john@doe.com',
 *   // Default values
 *   mail_obfuscated: true,
 *   data_obfuscated: true,
 * }
 *
 * the fields array of required fields looks like this:
 * [
 *   type,
 *   given,
 *   family,
 *   country,
 *   state,
 *   city,
 *   zip,
 *   streetaddr,
 *   phone,
 *   email,
 *   mail_obfuscated,
 *   data_obfuscated,
 * ]
 *
 * We can compute which fields are missing by comparing this array
 * and the array of keys in the values object.
 */
export const getBaseContact =
  (
    translator: TranslatorProps,
  ): ((Dispatch, GetState) => Promise<BaseContact>) =>
  async (dispatch: Dispatch, getState: GetState) => {
    const current: Organization = selectShopCurrent(getState(), true);
    const cartReselleeId: string = selectCartReselleeID(getState()) || '';
    const cartProductOwner: string = selectCartProductOwner(getState()) || '';

    const isReseller = get(current, 'reseller', false);

    let customer = selectOneCustomer(getState(), cartReselleeId);
    let owner = selectOneOrganization(getState(), cartReselleeId);
    let target = current;

    try {
      if (isReseller && cartReselleeId && current.id !== cartReselleeId) {
        if (!customer) {
          await dispatch(fetchOneCustomer(current.id, cartReselleeId));
          customer = selectOneCustomer(getState(), cartReselleeId);
        }
        // $FlowFixMe[incompatible-type]
        // $FlowFixMe[prop-missing]
        target = customer;
      } else if (cartProductOwner) {
        owner = selectOneOrganization(getState(), cartProductOwner);
        if (!owner) {
          await dispatch(fetchOneOrganization(cartProductOwner));
          owner = selectOneOrganization(getState(), cartProductOwner);
        }
        // $FlowFixMe[incompatible-type]
        // $FlowFixMe[prop-missing]
        target = owner;
      }
    } finally {
      if (!target) {
        // eslint-disable-next-line no-unsafe-finally
        throw new Error('Target Organization Unavailable.');
      }
    }

    const type: OrganizationType = target.type;

    const baseContactFields = getBaseContactFields(translator, type);
    const baseContactFieldsField = getBaseContactFieldsField(translator, type);

    // Initialize values object
    // $FlowFixMe[prop-missing]
    const values: BaseContact = {};

    // Assign default values
    baseContactFields
      .filter(field => 'defaultValue' in field)
      .forEach(field => {
        if (field.field) {
          values[field.field] = field.defaultValue;
        }
      });

    // Mapping selected data from state to field values ...
    baseContactFieldsField.forEach(field => {
      // $FlowFixMe[invalid-computed-prop]
      const mappedField = fieldsMapping[field] || field;

      const value = get(target, mappedField);
      if (value) {
        values[field] = value;
      }
    });

    // FIXME: should be a field mapping
    // Retrieve other extra parameters such as the VAT number
    const vatNumber = get(target, 'vat_number');
    if (vatNumber) {
      values.extra_parameters = {
        vat_number: vatNumber,
      };
    }
    // Checking for missing data in the values object...
    // const missing = _.difference(fields, Object.keys(values));
    return values;
  };

export const getOwnerContactExtraParams =
  (
    orgId: string,
    tlds: Array<string> = [],
  ): ((
    dispatch: Dispatch,
    getState: GetState,
  ) => Promise<{ [tld: string]: ?ExtraParams }>) =>
  async (dispatch: Dispatch, getState: GetState) => {
    // Load organizations owner extra params
    if (selectShouldFetchOwnerExtraParams(getState(), { orgId })) {
      await dispatch(contactbook.fetchOwnerContactExtraParams(orgId));
    }

    const extraParamsByTldAndRoles = contactbook.selectOwnerContactExtraParams(
      getState(),
      orgId,
    );

    const extraParams = Object.assign(
      {},
      ...Object.keys(extraParamsByTldAndRoles || {})
        .filter(tld => tlds.includes(tld))
        .map(tld => ({ [tld]: extraParamsByTldAndRoles?.[tld]?.owner })),
    );

    return extraParams;
  };

export const getProductsWithNewContactPayload =
  (
    translator: TranslatorProps,
    forcePatch: boolean = false,
  ): ((
    dispatch: Dispatch,
    getState: GetState,
  ) => Promise<mixed> | Promise<Array<any>>) =>
  async (dispatch: Dispatch, getState: GetState) => {
    const domainOwnerId = selectDomainOwnerId(getState());

    const productsToPatch = selectDomainProductsToPatchWithOwner(getState(), {
      sharingId: domainOwnerId,
      forcePatch,
    });

    const paasProductsToPatch = selectRawProductsArray(getState()).filter(
      p =>
        p.productType === 'paas' &&
        p.process === 'create' &&
        !!p.payload.domain?.fqdn,
    );

    // If no products to patch, exit
    if (productsToPatch.length === 0 && paasProductsToPatch.length === 0)
      return [];

    // Fetch owner extra params
    const prefillBaseContact = await dispatch(getBaseContact(translator));

    const hasResellee = selectCartReselleeID(getState()) !== '';

    const contactbookOrgId = hasResellee
      ? selectCartReselleeID(getState())
      : selectSetupOwner(getState());

    if (
      selectShouldFetchFavoriteContacts(
        getState(),
        selectSetupOwner(getState()),
      )
    ) {
      // required by `selectCurrentContactByRoles`
      await dispatch(
        contactbook.fetchContactsWithRole(selectSetupOwner(getState())),
      );
    }

    const ownerContactExtraParams = await dispatch(
      getOwnerContactExtraParams(
        contactbookOrgId,
        productsToPatch
          // $FlowFixMe[incompatible-call] update @gandi/cart ProductPayload
          .map(product => product.payload.tld)
          .concat(paasProductsToPatch.map(p => p.payload.domain?.tld || '')),
      ),
    );

    // Load the extra params for the contacts with role for the contactbookOrgId.
    // We load them for all the TLDs at once, it might be cheaper than to do a call for each TLD.
    const defaultContacts: Array<FavoriteContact> = (Object.values(
      contactbook.selectContactByRole(getState(), contactbookOrgId),
    ): any);
    await Promise.allSettled(
      defaultContacts
        .filter(contact =>
          selectShouldFetchOneContactExtraParams(getState(), {
            orgId: contactbookOrgId,
            contactId: contact.id,
          }),
        )
        .map(contact =>
          dispatch(
            contactbook.fetchContactExtraParams(contactbookOrgId, contact.id),
          ),
        ),
    );

    // need to split transfer and create because of favorite contacts
    const contactsCreate = selectCurrentContactByRoles(getState(), 'create');
    const contactsTransfer = selectCurrentContactByRoles(
      getState(),
      'transfer',
    );

    // $FlowFixMe improve @gandi/cart ProductData
    const productsData = productsToPatch.map<ProductData>(product => {
      // merge the steps product payload and the existing product payload
      const cleanPayload = omit(product.payload, [
        'admin',
        'tech',
        'bill',
        'trustee',
        'trustee_roles',
      ]);
      const contacts =
        product.process === 'create' ? contactsCreate : contactsTransfer;

      const contactsWithExtraParams = mapValues(contacts, (contact, role) =>
        contact?.id
          ? {
              ...contact,
              extra_parameters:
                contactbook.selectContactExtraParams(
                  getState(),
                  contactbookOrgId,
                  contact.id,
                )?.[product.payload.tld]?.[role] ?? {},
            }
          : contact,
      );

      const newPayload = {
        ...cleanPayload,
        ...contactsWithExtraParams,
        owner: {
          ...prefillBaseContact,
          extra_parameters: {
            ...prefillBaseContact.extra_parameters,
            ...(ownerContactExtraParams[product.payload.tld] || {}),
          },
        },
        lastCurrentOrgaId: domainOwnerId,
      };
      return { ...product, payload: newPayload };
    });

    // patch domain in paas payload

    const paasToPatch = paasProductsToPatch.map(p => {
      // merge the steps product payload and the existing product payload
      const cleanPayload = omit(p.payload.domain, [
        'admin',
        'tech',
        'bill',
        'trustee',
        'trustee_roles',
      ]);
      const contacts =
        p.process === 'create' ? contactsCreate : contactsTransfer;

      const tld = p.payload.domain?.tld;

      const contactsWithExtraParams = mapValues(contacts, (contact, role) =>
        contact?.id
          ? {
              ...contact,
              extra_parameters:
                contactbook.selectContactExtraParams(
                  getState(),
                  contactbookOrgId,
                  contact.id,
                  // $FlowFixMe improve @gandi/cart ProductData
                )?.[tld]?.[role] ?? {},
            }
          : contact,
      );

      const newDomainPayload = {
        ...cleanPayload,
        ...contactsWithExtraParams,
        owner: {
          ...prefillBaseContact,
          extra_parameters: {
            ...prefillBaseContact.extra_parameters,
            ...(ownerContactExtraParams[tld] || {}),
          },
        },
      };

      return { ...p, payload: { ...p.payload, domain: newDomainPayload } };
    });

    return productsData.concat(paasToPatch);
  };

/**
 * Autopatch domains contacts.
 *
 * PATCH the cart with a default owner contact if:
 * - orga id has changed (or was not previously set)
 *
 * PATCH the product with a prefilled owner contact.
 * - If if fails, display an Alert message to enter to the Wizard.
 */

export type SetupDomainContactsAction<R> = (props: {
  translator: TranslatorProps,
  forcePatch: boolean,
  noDryRun?: boolean,
}) => R;

export const setupDomainContacts: SetupDomainContactsAction<
  ThunkAction<Promise<void | PatchDomainContactsEndAction>>,
> =
  props =>
  async (
    dispatch: Dispatch,
    // getState: GetState
  ) => {
    const logger = logging(dispatch, loggerNamespace);

    const productsData = await dispatch(
      getProductsWithNewContactPayload(props.translator),
    );

    if (!productsData.length) {
      logger.info('No Domain Products need Contact PATCH.');
      return Promise.resolve();
    }

    // $FlowFixMe[not-an-array]
    dispatch(patchDomainContactsStart());

    return Promise.resolve().then(() => {
      const options = !props.noDryRun ? { headers: { 'X-Dry-Run': 0 } } : {};

      return (
        dispatch(updateProducts({ productsData, options }))
          .catch(err => {
            logger.error(
              { err },
              'Error during updating the cart with domains contacts data.',
            );
          })
          .then(() => !props.noDryRun && dispatch(getCart()))
          // $FlowFixMe[not-an-array]
          .then(() => dispatch(patchDomainContactsEnd()))
      );
    });
  };

/**
 * Perform the global check on tlds to get globalErrors.
 */

export type globalCheckAction<R> = (
  purchaseGroupId: PurchaseGroupId,
  process: Process,
  tlds: Array<Tld>,
  values: FormValues,
) => R;

export const globalCheck: globalCheckAction<ResourceThunkAction> =
  (purchaseGroupId, process, tlds, values) => (dispatch: Dispatch) => {
    // $FlowFixMe[cannot-spread-indexer]
    const data = {
      tlds,
      ...values,
    };
    return dispatch(
      globalCheckResource.createOne('/contactvalidation/global-check', data, {
        params: { process },
        // $FlowFixMe[incompatible-call]
        purchaseGroupId,
        label: 'global-check',
        // $FlowFixMe[incompatible-exact]
        on400: (response: FetchResponse<400, any>) => response,
        // $FlowFixMe[incompatible-exact]
        not400: (response: FetchResponse<any, any>) => {
          if (response.status === 200) {
            return Promise.resolve(response);
          }
          return Promise.reject(response);
        },
      }),
    );
  };

/**
 * Fetch tlds information from a list of fqdn,
 * and prepare the state.
 */
export type PrepareWizardAction<R> = () => R;

export const prepareWizard: PrepareWizardAction<ResourceThunkAction> =
  () => async (dispatch: Dispatch, getState: GetState) => {
    const productsWithDomainContactError =
      selectProductsWithDomainContactError(getState());

    const groupedProducts: {
      [key: PurchaseGroupId]: Array<ProductDomainInfo>,
      ...
    } = groupBy(productsWithDomainContactError, 'purchaseGroupId');

    // Call globalCheck() once per purchaseGroupId.
    await Promise.allSettled(
      // TODO: do all those products have an owner field?
      //       (I guess yes for the Change Owner process, how about the others?)
      toPairs(groupedProducts)
        .map(([purchaseGroupId, groupProducts]) => ({
          purchaseGroupId,
          tlds: uniq(groupProducts.map(product => product.tld)),
          owner: groupProducts[0].owner,
          process: groupProducts[0].process,
        }))
        .filter(({ tlds }) => tlds.length)
        .map(({ purchaseGroupId, tlds, owner, process }) =>
          dispatch(globalCheck(purchaseGroupId, process, tlds, owner)),
        ),
    );

    // If any of the tlds have a trustee service, fetch their prices.
    const state = getState();
    if (selectWizardHasTrusteeTlds(state) && !selectTrusteePrices(state)) {
      await dispatch(fetchCorporateTrusteePrices()).catch(() => {
        // pass
      });
    }

    // Only get 1 product per tuple [purchaseGroupId, process, tld], then sort them.
    const sortedProducts = sortBy(
      uniqBy(
        productsWithDomainContactError,
        product =>
          // $FlowFixMe[incompatible-type]
          `${product.purchaseGroupId},${product.process},${product.tld}`,
      ),
      'purchaseGroupId',
      'process',
      'tld',
    );

    return dispatch(prepareState({ products: sortedProducts }));
  };

export type ValidateDomainStepsByTldAction<R> = (
  step: Step,
  stepFormattedPayload: ProductPayload,
) => R;

// XXX: updateProducts from @gandi/cart is not typed, it dispatches a POST from redux-fetcher
type UpdateProductDispatchedAction = any;

export const validateDomainStepsByTld: ValidateDomainStepsByTldAction<
  ThunkAction<UpdateProductDispatchedAction | Promise<void>>,
> =
  (step, stepFormattedPayload) => (dispatch: Dispatch, getState: GetState) => {
    const {
      purchaseGroupId,
      domainInfo: { tld, process },
    } = step;

    // Select product ids of products with domain contact errors
    // Keep only products with the same tld as the product being validated
    const productsToPatch = selectProductsToPatchOrRemove(getState(), {
      purchaseGroupId,
      tld,
      // $FlowFixMe[incompatible-call]
      process,
    });

    const productsData = productsToPatch.map(product => {
      if (product.productType === 'paas') {
        return {
          ...product,
          payload: {
            ...product.payload,
            // $FlowFixMe[cannot-spread-inexact]
            domain: {
              ...product.payload.domain,
              ...stepFormattedPayload,
              trustee: stepFormattedPayload.trustee_roles.length > 0,
            },
          },
        };
      }

      // else productType === 'domain'
      return {
        ...product,
        // $FlowFixMe[cannot-spread-inexact]
        payload: {
          ...product.payload,
          ...stepFormattedPayload,
          trustee: stepFormattedPayload.trustee_roles.length > 0,
        },
      };
    });

    if (productsData.length) {
      // $FlowFixMe refine @gandi/cart type of productsData
      return dispatch(updateProducts({ productsData }));
    }

    return Promise.resolve();
  };

/**
 * Start the validation of all steps.
 *
 * Disclaimer:
 * All this code is largely too complex. Design is bad and should be review completely.
 * For now, trying to live with it.
 */

export type StartStepsValidationAction<R> = () => R;

export const startStepsValidation: StartStepsValidationAction<
  ThunkAction<Promise<void>>,
> = () =>
  async function validateAsync(dispatch: Dispatch, getState: GetState) {
    dispatch(validateContactsStart());

    const logger = logging(dispatch, loggerNamespace);
    let step: ?Step = null;

    try {
      for (; ; dispatch(incrementCurrentStepIndex())) {
        step = selectCurrentStep(getState());

        // Note: It is here instead of the for construct just to make Flow perform his null eliding correctly.
        if (!step) {
          break;
        }

        if (step.status === 'success' || step.trustee) {
          continue; // eslint-disable-line no-continue
        }

        const initialValues = selectCurrentStepInitialValues(getState());

        // The values to be sent to validation.
        const values = isEmpty(step.values) ? initialValues : step.values;

        const {
          purchaseGroupId,
          role,
          domainInfo: { fqdn, tld, process },
        } = step;

        const trusteeRole = selectTrusteeRoleFlag(getState());

        // validate values for the current step
        if (values && !trusteeRole) {
          // if we have several roles to validate (.ie, .it, .lv) we pass them
          const multiRoles = step.roles;
          if (multiRoles && multiRoles.length > 0) {
            // eslint-disable-next-line no-await-in-loop
            await dispatch(
              validateContactValues(
                // $FlowFixMe[incompatible-call]: fqdn & process are defined as maybe present in DomainInfo
                { fqdn, role, process },
                values,
                multiRoles,
              ),
            );
          } else {
            // else we validate as usual
            // eslint-disable-next-line no-await-in-loop
            await dispatch(
              validateContactValues(
                // $FlowFixMe[incompatible-call]: fqdn & process are defined as maybe present in DomainInfo
                { fqdn, role, process },
                values,
              ),
            );
          }
        }

        if (trusteeRole === role) {
          dispatch(addTrusteeToRole(fqdn, role, initialValues, step));
          dispatch(resetTrusteeRoleFlag());
        }

        // get the formatted product payload and patch the cart only if not empty
        // this returns an empty object if the 4 roles are not in a 'success' status
        const formattedPayload: ?ProductPayload = selectFormattedPayload(
          getState(),
          { purchaseGroupId, tld },
        );
        // patch only if the 4 roles have been defined
        if (formattedPayload) {
          // eslint-disable-next-line no-await-in-loop
          await dispatch(validateDomainStepsByTld(step, formattedPayload));
        }

        logger.info({ step }, 'Contact validation [OK]');
      }
    } catch (err) {
      logger.info({ step }, 'Contact validation [FAIL]');
    }

    dispatch(validateContactsEnd());
  };

/**
 * Remove products from the cart from the wizard.
 */

export type RemoveDomainAction<R> = (
  purchaseGroupId: PurchaseGroupId,
  tld: Tld,
  process: Process,
) => R;

// XXX: removeDomainsFromWizard is not typed due to missing redux-act definition
type RemoveDomainsFromWizardDispatchedAction = ReturnType<any>;

export const removeDomain: RemoveDomainAction<
  ThunkAction<RemoveDomainsFromWizardDispatchedAction | Promise<void>>,
> =
  (purchaseGroupId, tld, process) =>
  async (dispatch: Dispatch, getState: GetState) => {
    const logger = logging(dispatch, loggerNamespace);
    const state = getState();

    // Get the list of products in error for the current {purchaseGroupId, tld, process}.
    const productsToRemove = selectProductsToPatchOrRemove(state, {
      purchaseGroupId,
      tld,
      process,
    });

    if (!productsToRemove.length) {
      return Promise.resolve();
    }

    // Remove products from the cart.
    dispatch(removeDomainsStart());
    try {
      // $FlowFixMe refine @gandi/cart type of productsToRemove
      await dispatch(removeCartProducts(productsToRemove));
    } catch (err) {
      dispatch(removeDomainsFail());
      const message = 'Error when removing the products from the cart.';
      logger.error({ err }, message);
      throw new Error(message);
    }
    dispatch(removeDomainsSuccess());

    return dispatch(removeDomainsFromWizard(purchaseGroupId, tld, process));
  };
